import React, { useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Row, Form, Input, Button, Layout, Col, Spin } from 'antd';
import { CompanyLogo } from 'images';
import openpgpService from 'services/encryption/openpgpService';
import { changePasswordAPI } from 'services/settings/userAccount';
import { LOGGEDIN_USER_DATA } from 'global_config/constant-global';
import Message from 'shared_components/message';
import HttpCodeMessage from 'shared_components/message_http_status';
import wsService from 'services/websocket/WebSocketService';

import '../login/login.css';

const { Header, Content } = Layout;

function ChangePassword() {
	const formRef = useRef();
	const history = useHistory();
	const location = useLocation();
	const [loading, setLoading] = useState(false);

	// Get user data and encrypted password from location state
	const getUserData = () => {
		if (location.state && location.state.userData) {
			return location.state.userData;
		}
		const storedData = sessionStorage.getItem(LOGGEDIN_USER_DATA);
		return storedData ? JSON.parse(storedData) : null;
	};

	const userData = getUserData();
	const encryptedOldPassword = location.state && location.state.encryptedOldPassword ? location.state.encryptedOldPassword : null;

	async function handleSubmit() {
		const { getFieldsValue } = formRef.current;
		const { newPassword, confirmPassword } = getFieldsValue();

		// Validate passwords match
		if (newPassword !== confirmPassword) {
			Message.error('New passwords do not match');
			return;
		}

		if (!userData || !userData.userID) {
			Message.error('User session not found. Please login again.');
			history.push('/login');
			return;
		}

		if (!encryptedOldPassword) {
			Message.error('Session expired. Please login again.');
			history.push('/login');
			return;
		}

		try {
			setLoading(true);

			// Encrypt new password before sending
			const encryptedNewPassword = await openpgpService.encrypt(newPassword);

			// Call change password API using the encrypted password from login
			const response = await changePasswordAPI(
				userData.userID,
				encryptedOldPassword,
				encryptedNewPassword
			);

			if (response.status === 200) {
				// Clear sensitive data from location state
				if (location.state) {
					delete location.state.encryptedOldPassword;
					delete location.state.userData;
				}

				const httpMessageConfig = {
					message: response.message || 'Password changed successfully',
					status: response.status,
					duration: 3,
					onClose: () => {
						// Clear session and redirect to login
						wsService.disconnect();
						sessionStorage.clear();
						history.push('/login', {});
					}
				};
				HttpCodeMessage(httpMessageConfig);
			} else {
				Message.error(response.message || 'Failed to change password');
			}
		} catch (error) {
			console.error('Password change error:', error);
			Message.error('Failed to change password. Please try again.');
		} finally {
			setLoading(false);
		}
	}

	const passwordRules = [
		{ required: true, message: 'Please input your password!' },
		{ min: 6, message: 'Password must be at least 6 characters!' },
		{ max: 20, message: 'Password must not exceed 20 characters!' }
	];

	const confirmPasswordRules = [
		{ required: true, message: 'Please confirm your password!' },
		({ getFieldValue }) => ({
			validator(_, value) {
				if (!value || getFieldValue('newPassword') === value) {
					return Promise.resolve();
				}
				return Promise.reject(new Error('The two passwords do not match!'));
			},
		}),
	];

	return (
		<Layout>
			<Spin spinning={loading}>
				<Header style={{ borderBottom: 'none' }}>
					<Row>
						<Col span={24} />
					</Row>
				</Header>
				<Content>
					<div className="login-form">
						<div style={{ textAlign: 'center' }}>
							<img src={CompanyLogo} alt="logo" className="login-logo-image" />
							<h2 style={{ marginTop: 10, marginBottom: 10 }}>Change Password</h2>
							{userData && (
								<p style={{ color: '#666', marginBottom: 20 }}>
									User: {userData.userName}
								</p>
							)}
						</div>
						<Form onFinish={handleSubmit} ref={formRef} layout="vertical">
							<Form.Item
								name="newPassword"
								label="New Password"
								rules={passwordRules}
								hasFeedback
							>
								<Input.Password maxLength={20} placeholder="Enter new password" />
							</Form.Item>
							<Form.Item
								name="confirmPassword"
								label="Confirm New Password"
								dependencies={['newPassword']}
								rules={confirmPasswordRules}
								hasFeedback
							>
								<Input.Password maxLength={20} placeholder="Confirm new password" />
							</Form.Item>
							<Form.Item style={{ marginBottom: 0 }}>
								<Button type="primary" htmlType="submit" className="login-form-button" block>
									CHANGE PASSWORD
								</Button>
							</Form.Item>
						</Form>
					</div>
				</Content>
			</Spin>
		</Layout>
	);
}

export default ChangePassword;